import React from 'react';
import { Loader, Modal } from '@mantine/core';
import type { AdminPaymentlineView } from '../../../services/admin-payments/admin-payments.service';
import { AUDIT_ACTION_LABELS, METHOD_LABELS, STATUS_LABELS } from '../monitoring-page.constants';
import { formatDateTime, formatMoney } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

const CREDIT_STATUS_LABELS = {
  approved: 'אושר',
  declined: 'נדחה',
  unknown: 'לא ידוע',
} as const;

const formatResponse = (response: unknown): string => {
  try {
    return JSON.stringify(response, null, 2);
  } catch {
    return String(response);
  }
};

type PaymentlineAuditModalProps = Pick<
  UseMonitoringPage,
  'auditTarget' | 'auditRows' | 'isAuditLoading' | 'closeAuditModal'
>;

export const PaymentlineAuditModal: React.FC<PaymentlineAuditModalProps> = ({
  auditTarget,
  auditRows,
  isAuditLoading,
  closeAuditModal,
}) => {
  const confirmedCredit = auditRows.find(
    (row) =>
      row.confirmedCreditTransactionNumber ||
      row.confirmedCreditLast4 ||
      typeof row.confirmedCreditAmount === 'number' ||
      row.confirmedCreditCardName,
  );

  return (
    <Modal
      opened={auditTarget != null}
      onClose={closeAuditModal}
      title={auditTarget ? `#${auditTarget.id} ${auditTarget.orderCode}` : ''}
      centered
      size="lg"
      zIndex={1200}
      closeButtonProps={{ 'data-testid': 'monitoring-payment-audit-close' } as Record<string, string>}
    >
      {isAuditLoading ? (
        <div className={classes.loadingWrap}>
          <Loader />
        </div>
      ) : (
        <div className={classes.auditList}>
          {confirmedCredit && (
            <div className={classes.auditRow}>
              <div className={classes.auditHeader}>
                <span>{'פרטי אשראי מאושרים'}</span>
              </div>
              <div className={classes.auditValues}>
                {confirmedCredit.confirmedCreditTransactionNumber && (
                  <div>{`מספר עסקה: ${confirmedCredit.confirmedCreditTransactionNumber}`}</div>
                )}
                {confirmedCredit.confirmedCreditLast4 && (
                  <div>{`4 ספרות אחרונות: ${confirmedCredit.confirmedCreditLast4}`}</div>
                )}
                {typeof confirmedCredit.confirmedCreditAmount === 'number' && (
                  <div>{`סכום: ${formatMoney(confirmedCredit.confirmedCreditAmount)} ₪`}</div>
                )}
                {confirmedCredit.confirmedCreditCardName && (
                  <div>{`שם כרטיס: ${confirmedCredit.confirmedCreditCardName}`}</div>
                )}
              </div>
            </div>
          )}
          {auditRows.map((row) => (
            <div key={row.id} className={classes.auditRow}>
              <div className={classes.auditHeader}>
                <span>{AUDIT_ACTION_LABELS[row.action]}</span>
                <span>{formatDateTime(row.changedAt)}</span>
              </div>
              <div className={classes.auditMeta}>{row.actorName ?? 'מערכת'}</div>
              <div className={classes.auditValues}>
                {(row.fromStatus || row.toStatus) && (
                  <div>{`${STATUS_LABELS[(row.fromStatus ?? row.toStatus ?? 'new') as AdminPaymentlineView['status']]} -> ${STATUS_LABELS[(row.toStatus ?? row.fromStatus ?? 'new') as AdminPaymentlineView['status']]}`}</div>
                )}
                {(row.fromMethod || row.toMethod) && (
                  <div>{`${METHOD_LABELS[(row.fromMethod ?? row.toMethod ?? 'cash') as AdminPaymentlineView['method']]} -> ${METHOD_LABELS[(row.toMethod ?? row.fromMethod ?? 'cash') as AdminPaymentlineView['method']]}`}</div>
                )}
                {(typeof row.fromAmount === 'number' || typeof row.toAmount === 'number') && (
                  <div>{`${formatMoney(row.fromAmount ?? row.toAmount ?? 0)} -> ${formatMoney(row.toAmount ?? row.fromAmount ?? 0)} ₪`}</div>
                )}
                {row.action === 'transferred' && row.orderCode && (
                  <div>{`להזמנה ${row.orderCode}`}</div>
                )}
                {row.creditResponse && (
                  <>
                    <div>{`מקור: ${row.creditResponse.source} | שלב: ${row.creditResponse.phase === 'charge' ? 'חיוב' : 'בירור'}`}</div>
                    <div>{`תוצאה: ${CREDIT_STATUS_LABELS[row.creditResponse.status]}`}</div>
                    {row.creditResponse.errorMessage && <div>{`שגיאה: ${row.creditResponse.errorMessage}`}</div>}
                    {row.creditResponse.response != null && (
                      <details className={classes.creditResponseDetails}>
                        <summary>{'תגובה גולמית (רגישה מסוננת)'}</summary>
                        <pre>{formatResponse(row.creditResponse.response)}</pre>
                      </details>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {auditRows.length === 0 && (
            <div className={classes.empty}>{'אין היסטוריה עבור שורה זו.'}</div>
          )}
        </div>
      )}
    </Modal>
  );
};
